import React, {
  ReactElement, Ref, useEffect, useState,
} from 'react';
import {
  View,
  StyleSheet,
  Text,
  ScrollView,
  ActivityIndicator,
  FlexStyle,
  Platform,
  Keyboard,
  Dimensions,
  StatusBar,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Style from '../constants/Style';
import Colors from '../constants/Colors';
import DeleteIcon24 from './24pxIcons/DeleteIcon24';
import Button from './Button';
import PressableStyled from './PressableStyled';

const MIN_ANDROID_BOTTOM_INSET = 24;

interface SelectionModalProps {
  isVisible: boolean,
  hide: () => void,
  listContent: ReactElement,
  title: string,
  apply?: () => void,
  buttonTitle?: string,
  loading?: boolean,
  style?: FlexStyle,
  scrollViewRef?: Ref<ScrollView>,
  scrollEnabled?: boolean,
  disableSwipeToClose?: boolean,
  onModalHide?: () => void,
  onModalWillShow?: () => void,
}

const SelectionModal = ({
  isVisible, hide, listContent, title, apply, loading = false, style, scrollViewRef, buttonTitle,
  scrollEnabled = true, disableSwipeToClose, onModalHide, onModalWillShow,
}: SelectionModalProps) => {
  const { bottom: paddingBottom, top: paddingTop } = useSafeAreaInsets();
  const [backdropPressHideEnabled, setBackdropPressHideEnabled] = useState<boolean>(true);
  const [keyboardOffset, setKeyboardOffset] = useState<number>(0);
  const [androidBaseInset, setAndroidBaseInset] = useState<number>(0);

  useEffect(() => {
    if (isVisible) {
      setBackdropPressHideEnabled(false);
      const timeout = setTimeout(() => {
        setBackdropPressHideEnabled(true);
        clearTimeout(timeout);
      }, 375);
    }
  }, [isVisible]);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return undefined;
    }

    if (isVisible) {
      const screenHeight = Dimensions.get('screen').height;
      const windowHeight = Dimensions.get('window').height;
      const statusBarHeight = StatusBar.currentHeight ?? paddingTop;
      const estimatedNavBarInset = Math.max(0, screenHeight - windowHeight - statusBarHeight);
      const navBarInset = Math.min(estimatedNavBarInset, 48);
      setAndroidBaseInset(Math.max(paddingBottom, navBarInset, MIN_ANDROID_BOTTOM_INSET));
    }

    const showSubscription = Keyboard.addListener('keyboardDidShow', (event) => {
      setKeyboardOffset(event.endCoordinates.height);
    });

    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardOffset(0);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [isVisible, paddingBottom]);

  const onBackdropPress: () => void = () => (backdropPressHideEnabled ? hide() : null);

  return <Modal
    propagateSwipe
    {...{
      isVisible, onBackdropPress, onModalHide, onModalWillShow,
    }}
    onBackButtonPress={hide}
    onSwipeComplete={hide}
    swipeDirection={disableSwipeToClose ? undefined : 'down'}
    animationIn='slideInUp'
    animationOut='slideOutDown'
    statusBarTranslucent
    style={[Style.modal, styles.modal]}
    backdropColor={Colors.light.hereworksPrimaryGrey}
    backdropOpacity={0.40}
    backdropTransitionOutTiming={0}
  >
    <View style={[
      styles.wrapper,
      Platform.OS === 'android' && { paddingBottom: androidBaseInset + keyboardOffset },
    ]}>
      <View style={[
        styles.container,
        { paddingBottom: Platform.OS === 'ios' ? paddingBottom : 12 },
        apply && { maxHeight: styles.container.maxHeight + 80 },
        style,
      ]}>
        <Text style={styles.title} numberOfLines={1}>{title}</Text>
        <PressableStyled
          style={styles.closeButton}
          onPress={hide}
        >
          <DeleteIcon24 color={Colors.light.hereworksBlack600} />
        </PressableStyled>
        {scrollEnabled ? <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          ref={scrollViewRef}
        >
          {listContent}
        </ScrollView> : <View style={[styles.list, styles.listContent]}>{listContent}</View>}
        {apply ? <Button
          title={buttonTitle ?? 'Apply'}
          affirmative
          onPress={apply}
          style={styles.applyButton}
          children={loading && <ActivityIndicator style={styles.loadingIndicator} />}
        /> : <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />}
      </View>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    paddingTop: 0,
    paddingBottom: 0,
  },
  wrapper: {
    maxHeight: '90%',
  },
  container: {
    backgroundColor: '#FFF',
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    padding: 12,
    paddingTop: 20,
    maxHeight: 284,
  },
  title: {
    fontSize: 14,
    lineHeight: 17.5,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    alignSelf: 'center',
    marginLeft: 24,
    marginRight: 34,
  },
  list: {
    marginTop: 12,
    marginHorizontal: -12,
  },
  listContent: {
    paddingHorizontal: 24,
    flexGrow: 1,
  },
  closeButton: {
    position: 'absolute',
    end: 24,
    top: 16,
  },
  applyButton: {
    flex: 0,
    alignSelf: 'stretch',
    margin: 12,
    marginBottom: Platform.OS === 'android' ? 40 : undefined,
  },
  loadingIndicator: {
    alignSelf: 'center',
    position: 'absolute',
    end: '30%',
  },
});

export default SelectionModal;
